import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';
import { showToast } from '../../../../helpers/ToastifyHelper';
import { openFileInNewTab } from '../../../../helpers/openFileNewTab';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteIcon from '@mui/icons-material/Delete';

interface AbonelikSabitleriProps {
    sabit: any;
    onUpdate: (updatedValues: any) => Promise<void>;
}

const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const result = reader.result as string;
            const base64 = result.split(',')[1];
            resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
};

const AbonelikSabitleri: React.FC<AbonelikSabitleriProps> = ({ sabit, onUpdate }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [yeniAbonelikFile, setYeniAbonelikFile] = useState<File | null>(null);
    const [devirBasvuruFile, setDevirBasvuruFile] = useState<File | null>(null);

    const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, type: 'yeni' | 'devir') => {
        const file = e.target.files?.[0];
        if (!file) return;

        const allowedTypes = ['application/pdf'];
        if (!allowedTypes.includes(file.type)) {
            showToast('Sadece PDF dosyası yüklenebilir.', 'warning');
            return;
        }

        if (type === 'yeni') {
            setYeniAbonelikFile(file);
        } else {
            setDevirBasvuruFile(file);
        }
        e.target.value = '';
    };

    const handleView = (base64Data: string, fileName: string) => {
        openFileInNewTab(base64Data, fileName);
    };

    const handleSave = async () => {
        if (!yeniAbonelikFile && !devirBasvuruFile) {
            showToast('Lütfen en az bir dosya seçiniz.', 'warning');
            return;
        }

        setIsLoading(true);
        try {
            const updatedValues = { ...sabit };

            if (yeniAbonelikFile) {
                updatedValues.yeniAbonlkMesafeliSozlesmeBase64 = await fileToBase64(yeniAbonelikFile);
            }
            if (devirBasvuruFile) {
                updatedValues.devirBsvruMesafeliSozlesmeBase64 = await fileToBase64(devirBasvuruFile);
            }
            await onUpdate(updatedValues);

            setYeniAbonelikFile(null);
            setDevirBasvuruFile(null);
        } catch (error) {
            console.error('Kaydetme hatası:', error);
            showToast('Dosya yüklenirken bir hata oluştu.', 'error');
        } finally {
            setIsLoading(false);
        }
    };

    const hasChanges = yeniAbonelikFile !== null || devirBasvuruFile !== null;

    const rows = [
        {
            label: 'Yeni Abonelik Mesafeli Sözleşme',
            fileName: 'yeni-abonelik-mesafeli-sozlesme.pdf',
            file: yeniAbonelikFile,
            existingBase64: sabit?.yeniAbonlkMesafeliSozlesmeBase64,
            type: 'yeni' as const,
            onClear: () => setYeniAbonelikFile(null),
        },
        {
            label: 'Abonelik Devir Başvurusu Mesafeli Sözleşme',
            fileName: 'devir-basvurusu-mesafeli-sozlesme.pdf',
            file: devirBasvuruFile,
            existingBase64: sabit?.devirBsvruMesafeliSozlesmeBase64,
            type: 'devir' as const,
            onClear: () => setDevirBasvuruFile(null),
        },
    ];

    return (
        <>
            <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell><strong>Belge Adı</strong></TableCell>
                            <TableCell><strong>Durum</strong></TableCell>
                            <TableCell align="center"><strong>İşlemler</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row) => (
                            <TableRow key={row.type}>
                                <TableCell>{row.label}</TableCell>
                                <TableCell>
                                    {row.file ? (
                                        <Typography color="success.main" variant="body2">
                                            {row.file.name} (yeni seçildi)
                                        </Typography>
                                    ) : row.existingBase64 ? (
                                        <Typography color="info.main" variant="body2">
                                            Mevcut belge yüklü
                                        </Typography>
                                    ) : (
                                        <Typography color="text.secondary" variant="body2">
                                            Belge yüklenmemiş
                                        </Typography>
                                    )}
                                </TableCell>
                                <TableCell align="center">
                                    <label>
                                        <input
                                            type="file"
                                            accept=".pdf"
                                            hidden
                                            onChange={(e) => handleFileSelect(e, row.type)}
                                        />
                                        <IconButton component="span" color="primary" title="Dosya Seç">
                                            <UploadFileIcon />
                                        </IconButton>
                                    </label>
                                    {(row.existingBase64 || row.file) && (
                                        <IconButton
                                            color="info"
                                            title="Görüntüle"
                                            onClick={() => {
                                                if (row.file) {
                                                    const reader = new FileReader();
                                                    reader.onload = () => {
                                                        const base64 = (reader.result as string).split(',')[1];
                                                        handleView(base64, row.fileName);
                                                    };
                                                    reader.readAsDataURL(row.file);
                                                } else if (row.existingBase64) {
                                                    handleView(row.existingBase64, row.fileName);
                                                }
                                            }}
                                        >
                                            <VisibilityIcon />
                                        </IconButton>
                                    )}
                                    {row.file && (
                                        <IconButton color="error" title="Seçimi Kaldır" onClick={row.onClear}>
                                            <DeleteIcon />
                                        </IconButton>
                                    )}
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <Box textAlign={{ xs: 'center', md: 'right' }} mt={2}>
                <Button
                    type="button"
                    variant="contained"
                    disabled={!hasChanges || isLoading}
                    onClick={handleSave}
                    startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                >
                    {isLoading ? 'Yükleniyor...' : 'Kaydet'}
                </Button>
            </Box>
        </>
    );
};

export default AbonelikSabitleri;
